import React from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Divider,
  Tooltip,
  LinearProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

/**
 * Status Card Component
 */
const StatusCard = ({ icon, title, value, subtitle, status, color }) => (
  <Card
    variant="outlined"
    sx={{
      p: 2,
      height: '100%',
      borderLeft: 4,
      borderLeftColor: color || 'primary.main'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: `${color || 'primary'}.lighter`,
          color: color || 'primary.main'
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h6" fontWeight="bold" sx={{ my: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {status && (
          <Chip
            label={status.label}
            size="small"
            color={status.color}
            sx={{ mt: 1, height: 20, fontSize: '0.65rem' }}
          />
        )}
      </Box>
    </Box>
  </Card>
);

/**
 * Filing Status Row
 */
const FilingStatusRow = ({ type, label, lastFiled, nextDue, status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'ON_TIME': return 'success';
      case 'DUE_SOON': return 'warning';
      case 'OVERDUE': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ON_TIME': return <CheckCircleIcon fontSize="small" color="success" />;
      case 'DUE_SOON': return <ScheduleIcon fontSize="small" color="warning" />;
      case 'OVERDUE': return <CancelIcon fontSize="small" color="error" />;
      default: return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getStatusIcon(status)}
        <Typography variant="body2" fontWeight="medium">
          {label}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2">
          Last filed: {lastFiled || 'N/A'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Next due: {nextDue || 'N/A'}
        </Typography>
      </Box>
      <Chip
        label={status.replace('_', ' ')}
        size="small"
        color={getStatusColor(status)}
        sx={{ minWidth: 80, height: 20, fontSize: '0.65rem' }}
      />
    </Box>
  );
};

/**
 * Tax Profile Summary Component
 * Displays organization's tax registration and compliance status
 *
 * @param {Object} props
 * @param {Object} props.profile - Tax profile data
 * @param {Array} props.deadlines - Upcoming deadlines
 */
const TaxProfileSummary = ({ profile, deadlines }) => {
  if (!profile) {
    return (
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Tax profile not available. Register for Corporate Tax to get started.
        </Typography>
      </Card>
    );
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Grid container spacing={3}>
      {/* CT Registration Status */}
      <Grid item xs={12} sm={6} md={3}>
        <StatusCard
          icon={<AccountBalanceIcon />}
          title="Corporate Tax"
          value={profile.ctRegistered ? 'Registered' : 'Not Registered'}
          subtitle={profile.ctRegistered ? `TRN: ${profile.trn}` : 'Registration required'}
          status={profile.ctRegistered
            ? { label: 'Active', color: 'success' }
            : { label: 'Action Required', color: 'warning' }
          }
          color={profile.ctRegistered ? 'success.main' : 'warning.main'}
        />
      </Grid>

      {/* VAT Status */}
      <Grid item xs={12} sm={6} md={3}>
        <StatusCard
          icon={<ReceiptLongIcon />}
          title="VAT Registration"
          value={profile.vatRegistered ? 'Registered' : 'Not Registered'}
          subtitle={profile.vatRegistered ? `Category: ${profile.vatCategory}` : null}
          status={profile.vatRegistered
            ? { label: 'Active', color: 'success' }
            : { label: 'Optional', color: 'default' }
          }
          color={profile.vatRegistered ? 'success.main' : 'grey.500'}
        />
      </Grid>

      {/* Revenue */}
      <Grid item xs={12} sm={6} md={3}>
        <StatusCard
          icon={<TrendingUpIcon />}
          title="Last Audited Revenue"
          value={formatCurrency(profile.lastAuditedRevenue)}
          subtitle={`FY ${profile.lastAuditYear}`}
          color="info.main"
        />
      </Grid>

      {/* Estimated Tax */}
      <Grid item xs={12} sm={6} md={3}>
        <StatusCard
          icon={<AccountBalanceIcon />}
          title="Est. CT Liability"
          value={formatCurrency(profile.estimatedCtLiability?.estimatedTax || 0)}
          subtitle={`Tax Period ${profile.estimatedCtLiability?.taxPeriod}`}
          color="error.main"
        />
      </Grid>

      {/* Filing Status Card */}
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Filing Status
          </Typography>
          <Divider sx={{ my: 1 }} />

          {profile.ctRegistered && profile.filingStatus?.ct && (
            <FilingStatusRow
              type="CT"
              label="Corporate Tax Return"
              lastFiled={profile.filingStatus.ct.lastFiledPeriod}
              nextDue={profile.filingStatus.ct.nextDueDate}
              status={profile.filingStatus.ct.status}
            />
          )}

          {profile.vatRegistered && profile.filingStatus?.vat && (
            <FilingStatusRow
              type="VAT"
              label="VAT Return"
              lastFiled={profile.filingStatus.vat.lastFiledPeriod}
              nextDue={profile.filingStatus.vat.nextDueDate}
              status={profile.filingStatus.vat.status}
            />
          )}

          {!profile.ctRegistered && !profile.vatRegistered && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No filing obligations yet. Register for CT/VAT to track filing status.
            </Typography>
          )}
        </Card>
      </Grid>

      {/* Small Business Relief Status */}
      {profile.ctRegistered && (
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Small Business Relief Eligibility
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              {profile.eligibleForSmallBusinessRelief ? (
                <>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Eligible for Small Business Relief
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Revenue ≤ AED 3 million threshold
                    </Typography>
                  </Box>
                  {profile.smallBusinessElectionActive ? (
                    <Chip label="Election Active" size="small" color="success" sx={{ ml: 'auto' }} />
                  ) : (
                    <Chip label="Not Elected" size="small" color="warning" variant="outlined" sx={{ ml: 'auto' }} />
                  )}
                </>
              ) : (
                <>
                  <CancelIcon color="disabled" />
                  <Box>
                    <Typography variant="body2">
                      Not Eligible
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Revenue exceeds AED 3 million threshold
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Card>
        </Grid>
      )}

      {/* Tax Agent Status */}
      {profile.ctRegistered && (
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Tax Agent Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              {profile.taxAgentAppointed ? (
                <>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Thaklis appointed as Tax Agent
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      TAN: TA-XXXX (valid until renewal)
                    </Typography>
                  </Box>
                  <Chip label="Active" size="small" color="success" sx={{ ml: 'auto' }} />
                </>
              ) : (
                <>
                  <CancelIcon color="disabled" />
                  <Box>
                    <Typography variant="body2">
                      No Tax Agent Appointed
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Appoint Thaklis to file on your behalf
                    </Typography>
                  </Box>
                  <Chip label="Not Appointed" size="small" variant="outlined" sx={{ ml: 'auto' }} />
                </>
              )}
            </Box>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default TaxProfileSummary;
